/**
 * Normalizes VisualNavigation data from different sources into a consistent structure
 * Priority: props > component > slot
 *
 * @param {Object} options - Data sources
 * @param {Object} options.slotData - Data from slot query (fusion pattern)
 * @param {Object} options.componentData - Data from component query (legacy pattern)
 * @param {number} options.mediaCollectionIndex - Index of mediaCollection to use (default: 0)
 * @returns {Object} Normalized data with consistent structure including component data and item list
 */

const getCustomProp = (entry, key) => {
  if (entry?.customProps) {
    try {
      const props = JSON.parse(entry.customProps);
      if (props && typeof props === 'object' && !Array.isArray(props)) {
        return props[key];
      }
      return undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

export const normalizeVisNavData = ({ slotData, componentData, mediaCollectionIndex = 0 }) => {
  // Priority: component data first (legacy pattern), then fall back to slot
  if (componentData?.component) {
    const component = componentData.component;
    return {
      source: 'component',
      component: {
        id: component.id,
        title: component.title,
        componentName: component.componentName,
        overflowBehavior: component.overflowBehavior,
        theme: component.theme,
        itemTheme: component.itemTheme
      },
      list: component.visualNavigationList || []
    };
  }

  // Fall back to slot data (fusion pattern)
  if (slotData?.slot) {
    const slot = slotData.slot;
    const mediaCollections = slot.mediaCollections || [];
    const fallbackMediaCollection = mediaCollections[0] || null;
    const mediaCollection = mediaCollections[mediaCollectionIndex] || fallbackMediaCollection;
    const media = mediaCollection?.media || [];

    const overflowBehavior = getCustomProp(mediaCollection, 'overflowBehavior');

    // Transform media array to visualNavigationList format
    const transformedList = media.map((mediaItem, index) => ({
      id: mediaItem.entryId || String(index + 1),
      title: mediaItem.title || '',
      altText: mediaItem.sourceDesktop?.[0]?.altText || '',
      description: mediaItem.description || '',
      link: mediaItem.sourceDesktop?.[0]?.hyperlink || '',
      previewImage: {
        damDownloadedContent: {
          url: mediaItem.sourceDesktop?.[0]?.uri || ''
        }
      }
    }));

    return {
      source: 'slot',
      component: {
        id: slot.id,
        title: mediaCollection?.title || '',
        componentName: 'VisualNavigation',
        overflowBehavior: overflowBehavior || 'Wrap',
        theme: mediaCollection?.theme || null,
        itemTheme: null
      },
      list: transformedList
    };
  }

  // No data available
  return null;
};
